/**
 * UIMaterial / LitPanel — the engine-material UI keystone (BL-37, ART §9).
 *
 * This is the reusable primitive that draws a **lit CRT panel** onto the
 * UI-chrome canvas, using the arena's OWN material language — the real
 * `EmissiveMaterial.emissiveFill` (the §6.1 hot-core→mid→rim falloff) and the
 * real `GlowSpriteCache.glowCache` (the cached additive halo the enemy glows
 * use). It is deliberately NOT a CSS lookalike: the HUD and the arena speak one
 * material language because they share these two functions.
 *
 * A panel reads as a surface lit from its edge, not a flat rectangle:
 *   1. a faint inner gradient fill (the lit CRT surface), then
 *   2. an emissive rounded-rect edge stroke filled with `emissiveFill` (hot
 *      edge + falloff — the §6.1 thinking applied to a frame), then
 *   3. a cached `glowCache` additive bloom seed along the edge so S87 can route
 *      this single canvas through the shared WebGL bloom and it lights exactly
 *      like the arena.
 *
 * State communicates through the material (ART §9): `active` panels glow
 * brighter, `disabled` panels dim — driven by `UI_PANEL_MATERIAL.stateIntensity`.
 *
 * `drawLitPanel` is the API BL-54 (card material / rarity tiers) and BL-55
 * (tower-preview chrome) consume — `{rect, colour, state, intensity}`. It is a
 * pure draw call (no retained state) and tolerates a null/headless context so
 * tests and degraded environments never throw.
 */
import { emissiveFill, deriveRamp } from "../rendering/EmissiveMaterial.ts";
import { glowCache } from "../rendering/GlowSpriteCache.ts";
import { UI_PANEL_MATERIAL, type PanelState } from "./uiTokens.ts";

export interface UIRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface LitPanelOptions {
  /** Panel rect in canvas pixels (already DPR-scaled by the caller). */
  rect: UIRect;
  /** Edge colour — defaults to the material's green identity. Pass an element
   *  colour (e.g. a tower hue) for BL-54/55 tinted chrome. */
  colour?: string | undefined;
  /** Material state — `active` brighter, `disabled` dim. */
  state?: PanelState;
  /** Extra intensity multiplier on top of the state (0..N). Drives the
   *  value-flash glint and hover lift. Default 1. */
  intensity?: number;
  /** Override corner radius (px). Default from the material token. */
  cornerRadius?: number;
  /** Override edge-glow radius (px). Default from the material token. */
  edgeRadius?: number;
  /** Draw the faint inner lit-surface gradient. Default true. Set false for a
   *  pure edge (e.g. an inline element that sits over existing content). */
  fillSurface?: boolean;
  /** Draw the additive `glowCache` bloom seed. Default true. The BL-44 degrade
   *  rung sets this false → flat lit edge, no per-edge glow stamps, under load. */
  glow?: boolean;
}

/** Trace a rounded-rect path on `ctx` (no fill/stroke — caller decides). */
export function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/**
 * Effective edge intensity for a panel = state multiplier × caller intensity,
 * clamped to a sane ceiling so a runaway flash can't blow the canvas to white.
 * Pure — exported so the chrome layer and tests can reason about it without a
 * canvas.
 */
export function effectiveIntensity(
  state: PanelState = "default",
  intensity = 1,
): number {
  const base = UI_PANEL_MATERIAL.stateIntensity[state];
  return Math.max(0, Math.min(3, base * intensity));
}

/**
 * Draw one lit panel. No-op (silent) if `ctx` is null/headless — callers in the
 * live game pass a real 2D context; tests and the BL-44 degrade rung may not.
 */
export function drawLitPanel(
  ctx: CanvasRenderingContext2D | null,
  opts: LitPanelOptions,
): void {
  if (!ctx) return;
  const m = UI_PANEL_MATERIAL;
  const { x, y, w, h } = opts.rect;
  if (w <= 0 || h <= 0) return;

  const colour = opts.colour ?? m.edgeColor;
  const state = opts.state ?? "default";
  const intensity = effectiveIntensity(state, opts.intensity ?? 1);
  const corner = opts.cornerRadius ?? m.cornerRadius;
  const edgeRadius = opts.edgeRadius ?? m.edgeRadius;
  const fillSurface = opts.fillSurface ?? true;
  const ramp = deriveRamp(colour);

  ctx.save();

  // 1 — the lit CRT surface: a faint vertical inner gradient. Clipped to the
  // rounded rect so the fill never leaks past the panel silhouette.
  if (fillSurface) {
    roundedRectPath(ctx, x, y, w, h, corner);
    ctx.save();
    ctx.clip();
    const grad = ctx.createLinearGradient(x, y, x, y + h);
    grad.addColorStop(0, m.innerGradient[0]);
    grad.addColorStop(1, m.innerGradient[1]);
    ctx.fillStyle = grad;
    ctx.globalAlpha = 0.9;
    ctx.fillRect(x, y, w, h);
    ctx.restore();
  }

  // 2 — the emissive edge: stroke the rounded rect with the §6.1 hot-edge fill.
  // The edge is the "body"; `emissiveFill` gives it the core→mid→rim volume so
  // the border reads as neon, not a 1-px line.
  const cx = x + w / 2;
  const cy = y + h / 2;
  const bodyR = Math.max(w, h) / 2;
  roundedRectPath(ctx, x, y, w, h, corner);
  ctx.lineWidth = 1.5;
  ctx.globalAlpha = Math.min(1, m.edgeOpacity * intensity + 0.2);
  ctx.strokeStyle = emissiveFill(ctx, cx, cy, bodyR, ramp);
  ctx.stroke();

  // 3 — the additive bloom seed: cached `glowCache` sprites stamped along the
  // edge (corners + edge midpoints). This is the SAME lever the arena enemy
  // halos use — genuinely lit, cheap (cached), and the single source S87 routes
  // through the shared WebGL bloom. Drawn additively so it reads as emission.
  // Skipped by the BL-44 degrade rung (`glow: false`) → flat lit edge only.
  if (opts.glow === false) {
    ctx.restore();
    return;
  }
  ctx.globalCompositeOperation = "lighter";
  const glowR = edgeRadius * Math.min(1.4, 0.6 + intensity * 0.5);
  const glowA = Math.min(0.85, m.edgeOpacity * intensity * 0.8);
  const seeds: [number, number][] = [
    [x, y],
    [x + w, y],
    [x, y + h],
    [x + w, y + h],
    [cx, y],
    [cx, y + h],
    [x, cy],
    [x + w, cy],
  ];
  for (const [sx, sy] of seeds) {
    glowCache.draw(ctx, ramp.core, glowR, sx, sy, glowA);
  }

  ctx.restore();
}

/** Options for {@link drawCircuitEtch}. */
export interface CircuitEtchOptions {
  /** Etch L-tick reach in px (the corner accent length). Default 14. */
  reach?: number;
  /** Inset from the rect corner in px. Default 4. */
  inset?: number;
  /** Stroke alpha 0..1 (scaled by the bright filament). Default 0.8. */
  alpha?: number;
  /** Stamp an additive `glowCache` "via" dot at each corner (bloom fuel). */
  glow?: boolean;
}

/**
 * Draw the refined **circuit-etch** corner accent (BL-59) — the engine-material
 * replacement for the DEMOTED ⌐ corner-bracket motif. At each of the four
 * corners it etches a short L of trace: a dim grounded line (`rim`) under a
 * bright filament (`core`), plus an optional additive "via" dot the shared
 * bloom catches. It reads as an *etched circuit edge*, not a drawn bracket —
 * the unified card frame's "filigree" (cyber, not Pokémon-gold-ornate).
 *
 * Pure draw, no retained state, null/headless-ctx safe (silent no-op).
 */
export function drawCircuitEtch(
  ctx: CanvasRenderingContext2D | null,
  rect: UIRect,
  colour: string,
  opts: CircuitEtchOptions = {},
): void {
  if (!ctx) return;
  const { x, y, w, h } = rect;
  if (w <= 0 || h <= 0) return;
  const reach = opts.reach ?? 14;
  const inset = opts.inset ?? 4;
  const alpha = opts.alpha ?? 0.8;
  const ramp = deriveRamp(colour);

  const x0 = x + inset;
  const y0 = y + inset;
  const x1 = x + w - inset;
  const y1 = y + h - inset;
  // [corner x, corner y, dx toward inside, dy toward inside]
  const corners: [number, number, number, number][] = [
    [x0, y0, 1, 1],
    [x1, y0, -1, 1],
    [x0, y1, 1, -1],
    [x1, y1, -1, -1],
  ];

  ctx.save();
  for (const [cxk, cyk, dx, dy] of corners) {
    // grounded under-line (dim) then the bright filament over it.
    ctx.lineCap = "round";
    ctx.strokeStyle = ramp.rim;
    ctx.globalAlpha = alpha * 0.5;
    ctx.lineWidth = 2.4;
    ctx.beginPath();
    ctx.moveTo(cxk + dx * reach, cyk);
    ctx.lineTo(cxk, cyk);
    ctx.lineTo(cxk, cyk + dy * reach);
    ctx.stroke();

    ctx.strokeStyle = ramp.core;
    ctx.globalAlpha = alpha;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cxk + dx * reach, cyk);
    ctx.lineTo(cxk, cyk);
    ctx.lineTo(cxk, cyk + dy * reach);
    ctx.stroke();

    if (opts.glow) {
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      glowCache.draw(ctx, ramp.core, 7, cxk, cyk, alpha * 0.6);
      ctx.restore();
    }
  }
  ctx.restore();
}
